import templates from '/app/templates'
import BaseChannel from './base'
import { widgetManager } from '../../src/widgetManager/index.js'

export default class GenesysFaqChannel extends BaseChannel {
  constructor(settings, { billingService, widgetContainer, texts, widgetIndex, genesysKnowledgeBaseService, singleChannelBehavior }) {
    super(settings, { widgetContainer })
    this.texts = texts
      .filter((x) => x.type === 'knowledgeBase')
      .reduce(function (map, obj) {
        map[obj.key] = obj.value
        return map
      }, {})
    this.billingService = billingService

    this.fields = settings.fields || []
    this.deploymentId = this.params.deployment
    this.configuration = this.params.configuration || {}

    this.channelType = 'faq'
    this.visitorId = this.widgetContainer.token

    this.widgetIndex = widgetIndex
    this.genesysKnowledgeBaseService = genesysKnowledgeBaseService
    this.genesysKnowledgeBaseService.setDeploymentId(this.params.deployment)

    this.articles = {}
    this.searchResults = null
    this.isCategoriesAvailable = false
    this.singleChannelBehavior = singleChannelBehavior
  }

  setup() {
    this.styleNamespace = 'cce-widget-faq'
  }

  async open() {
    this.$element = document.createElement('div')
    this.$element.classList.add(this.styleNamespace)
    this.$quickReplayElement = document.createElement('div')
    this.$quickReplayElement.classList.add('this.styleNamespace')

    await this.render()

    const widgetWindow = document.querySelector('.cce-widget__window')
    if (widgetWindow.classList.contains('cce-widget__window--open')) {
      this.isActive = true
    }
  }

  async getCategories(parentId = null) {
    const categories = await this.genesysKnowledgeBaseService.getCategories(parentId)
    this.isCategoriesAvailable = categories.length > 0
    if (parentId === null) {
      this.categories = categories
    }
    return categories
  }

  async getArticles(categoryId) {
    const articles = await this.genesysKnowledgeBaseService.getDocuments([categoryId], 10, 1)
    return articles
  }

  async getArticle(articleId) {
    const article = await this.genesysKnowledgeBaseService.getDocument(articleId)
    return article
  }

  async search(query, queryType = 'ManualSearch', pageSize = 100, pageNumber = 1) {
    const searchResults = await this.genesysKnowledgeBaseService.search(query, queryType, pageSize, pageNumber)
    const sortedResults = searchResults.results.sort((a, b) => b.confidence - a.confidence).map((x) => x.document)
    return { results: sortedResults, total: searchResults.total, query: query }
  }

  async suggestions(query, pageSize = 3) {
    const suggestions = await this.genesysKnowledgeBaseService.suggestions(query, pageSize)
    return suggestions.map((x) => x.matchedPhrase)
  }

  async render() {
    //TODO: add loading state

    this.observeEvents()
    this.observeState()

    await this.genesysKnowledgeBaseService.createGuestSession(this.visitorId, window.location.href)
    await this.getCategories() //fill categories cache
    this.openCategory('-1')
    const searchInput = document.querySelector('.cce-widget-faq__body__content__search div input')
    if (searchInput) {
      searchInput.focus()
    }
  }

  isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
  }

  openCategory(categoryId, articles = []) {
    if (categoryId !== '-1') {
      this.widgetContainer.triggerPublicEvent('widget_business_success', { state: widgetManager.getState(), confirmed: true })
    }

    const templateVariables = this.getTemplateVariables()
    let subCategories

    let currentCategory = this.categories.find((x) => x.id === categoryId)
    let parentCategoryId = '-1'
    if (currentCategory && currentCategory.parentCategory) {
      parentCategoryId = currentCategory.parentCategory.id
    }

    const element = document.querySelector('.cce-widget__window__header__faq--back')
    if (categoryId !== '-1') {
      subCategories = this.categories.filter((x) => x.parentCategory && x.parentCategory.id === categoryId)
    } else {
      subCategories = this.categories.filter((x) => !x.parentCategory)
    }

    this.$element.innerHTML = templates.genesysFaqTemplate(
      Object.assign({}, templateVariables, {
        categories: subCategories,
        articles: articles,
        parentCategoryId: parentCategoryId,
        isRootCategory: categoryId === '-1',
      })
    )
    element.removeAttribute('data-id')
    element.removeAttribute('data-type')
    if (categoryId === '-1') {
      if ((this.singleChannelBehavior === 'open_channel' && this.widgetContainer.settings.widgets[this.widgetIndex].channels.length) === 1) {
        const faqBackArrow = document.querySelector('[name=ccewHandleFaqChannel]')
        faqBackArrow.classList.add(`cce-widget__window__header__faq--back--hide`)
      }
    }
    if (categoryId !== '-1') {
      element.setAttribute('data-id', `${parentCategoryId}`)
      element.dataset.type = 'category'
    }
  }

  openArticle(article, fromSearch = false) {
    this.widgetContainer.triggerPublicEvent('widget_business_success', { state: widgetManager.getState(), confirmed: true })
    const templateVariables = this.getTemplateVariables()

    const generateHTML = (blocks) => {
      const processTextBlock = (textBlock) => {
        let text = textBlock.text.text
        if (textBlock.text.marks) {
          if (textBlock.text.marks.includes('Bold')) text = `<strong>${text}</strong>`
          if (textBlock.text.marks.includes('Italic')) text = `<em>${text}</em>`
          if (textBlock.text.marks.includes('Underline')) text = `<u>${text}</u>`
          if (textBlock.text.marks.includes('Strikethrough')) text = `<del>${text}</del>`
        }
        if (textBlock.text.hyperlink) {
          text = `<a href="${textBlock.text.hyperlink}" target="_blank">${text}</a>`
        }
        if (textBlock.text.properties) {
          const styles = []
          if (textBlock.text.properties.textColor) {
            styles.push(`color: ${textBlock.text.properties.textColor}`)
          }
          if (textBlock.text.properties.backgroundColor) {
            styles.push(`background-color: ${textBlock.text.properties.backgroundColor}`)
          }
          if (styles.length > 0) {
            text = `<span style="${styles.join(';')}">${text}</span>`
          }
        }
        return text
      }

      const processListItems = (items) => {
        return items
          .map((item) => {
            let content = item.blocks
              .map((block) => {
                if (block.type === 'Text') {
                  return processTextBlock(block)
                } else if (block.type === 'UnorderedList' || block.type === 'OrderedList') {
                  return processListBlock(block)
                }
                return ''
              })
              .join('')
            return `<li>${content}</li>`
          })
          .join('')
      }

      const processListBlock = (block) => {
        const tag = block.type === 'UnorderedList' ? 'ul' : 'ol'
        let listStyle = ''
        if (block.list && block.list.properties && block.list.properties.unorderedType) {
          listStyle = block.list.properties.unorderedType.toLowerCase()
        }
        const style = listStyle ? ` style="list-style-type: ${listStyle}"` : ''
        return `<${tag}${style}>${processListItems(block.list.blocks)}</${tag}>`
      }

      return blocks
        .map((block) => {
          switch (block.type) {
            case 'Paragraph':
              const paragraphStyle = []
              if (block.paragraph.properties) {
                if (block.paragraph.properties.align) {
                  paragraphStyle.push(`text-align: ${block.paragraph.properties.align.toLowerCase()}`)
                }
              }
              const styleAttr = paragraphStyle.length ? ` style="${paragraphStyle.join(';')}"` : ''

              let tag = 'p'
              if (block.paragraph.properties && block.paragraph.properties.fontType && block.paragraph.properties.fontType.startsWith('Heading')) {
                tag = `h${block.paragraph.properties.fontType.slice(-1)}`
              }

              const content = block.paragraph.blocks
                .map((b) => {
                  if (b.type === 'Text') return processTextBlock(b)
                  if (b.type === 'Image') {
                    if (!b.image.properties) {
                      //set default values
                      b.image.properties = {
                        altText: '',
                        width: 100,
                      }
                    }
                    return `<img src="${b.image.url}" alt="${b.image.properties.altText || ''}" ${
                      b.image.properties.width ? `width="${b.image.properties.width}"` : ''
                    }>`
                  }
                  if (b.type === 'Video') {
                    return `<iframe src="${b.video.url}" ${b.video.properties.width ? `width="${b.video.properties.width.value}"` : ''} ${
                      b.video.properties.height ? `height="${b.video.properties.height.value}"` : ''
                    } frameborder="0" allowfullscreen></iframe>`
                  }
                  return ''
                })
                .join('')

              return `<${tag}${styleAttr}>${content}</${tag}>`

            case 'UnorderedList':
            case 'OrderedList':
              return processListBlock(block)

            case 'Table':
              return `<table style="width: ${block.table.properties.width}%">${block.table.rows
                .map(
                  (row) =>
                    `<tr>${row.cells
                      .map((cell) => {
                        const cellAttrs = []
                        if (cell.properties.colSpan > 1) {
                          cellAttrs.push(`colspan="${cell.properties.colSpan}"`)
                        }
                        if (cell.properties.width) {
                          cellAttrs.push(`style="width: ${cell.properties.width}%"`)
                        }
                        return `<td ${cellAttrs.join(' ')}>${cell.blocks.map((b) => (b.type === 'Text' ? processTextBlock(b) : '')).join('')}</td>`
                      })
                      .join('')}</tr>`
                )
                .join('')}</table>`

            default:
              return ''
          }
        })
        .join('')
    }
    article.content = generateHTML(article.variations[0].body.blocks)

    this.$element.innerHTML = templates.genesysFaqArticleTemplate(
      Object.assign({}, templateVariables, {
        article: article,
        fromSearch: fromSearch,
      })
    )
    const element = document.querySelector('.cce-widget__window__header__faq--back')
    element.removeAttribute('data-id')
    element.removeAttribute('data-type')
    if (fromSearch) {
      element.dataset.type = 'search'
    } else if (!fromSearch) {
      if (article.category) {
        element.setAttribute('data-id', `${article.category.id}`)
        element.dataset.type = 'category'
      } else {
        element.dataset.type = 'search'
      }
    }

    const element2 = document.querySelector('.cce-widget-faq__body__content__article-expand')
    element2.addEventListener('click', () => {
      const widgetWindow = document.querySelector('.cce-widget__window')
      widgetWindow.classList.toggle('extendView')
    })
  }

  openSearchResults(searchResults) {
    this.widgetContainer.triggerPublicEvent('widget_business_success', { state: widgetManager.getState(), confirmed: true })
    const articles = searchResults.results
    const total = searchResults.total
    const query = searchResults.query

    this.$element.innerHTML = templates.genesysFaqSearchResultsTemplate(
      Object.assign({}, this.getTemplateVariables(), { articles: articles, total: total, query: query })
    )
    const element = document.querySelector('.cce-widget__window__header__faq--back')
    element.removeAttribute('data-id')
    element.removeAttribute('data-type')
    element.setAttribute('data-id', `-1`)
    element.dataset.type = 'category'
  }

  getTemplateVariables() {
    return {
      styleNamespace: this.styleNamespace,
      agreementQuestions: [],
      additionalFields: this.fields || [],
      deploymentId: this.params.deployment,
      texts: this.texts,
      isIOS: this.isIOS(),
    }
  }

  async clickOnSuggestion(suggestion) {
    this.searchResults = await this.search(suggestion, 'Suggestion', 3)
    const firstSuggestion = this.searchResults.results[0]
    const article = await this.getArticle(firstSuggestion.id)
    this.openArticle(article, true)
    this.showBackArrow()
  }

  observeState() {
    const config = { attributes: true, attributeOldValue: true }

    const handleFaqChannelClassChange = (mutationsList) => {
      for (let mutation of mutationsList) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const targetNode = document.querySelector('.cce-widget-faq')
          if (targetNode.classList.contains('open')) {
            const searchInput = document.querySelector('.cce-widget-faq__body__content__search div input')
            if (searchInput) {
              searchInput.focus()
            }
          }
        }
      }
    }

    const observer = new MutationObserver(handleFaqChannelClassChange)
    observer.observe(this.$element, config)
  }

  showBackArrow() {
    const faqBackArrow = document.querySelector('[name=ccewHandleFaqChannel]')

    if (faqBackArrow) {
      faqBackArrow.classList.remove(`cce-widget__window__header__faq--back--hide`)
    }
  }

  observeEvents() {
    let debounceTimer

    this.$element.addEventListener('click', async (event) => {
      const target = event.target
      if (target.dataset.type === 'category') {
        this.showBackArrow()
        const articles = await this.getArticles(target.dataset.id)
        this.openCategory(target.dataset.id, articles)
        this.articles[target.dataset.id] = articles
      } else if (target.dataset.type === 'article') {
        this.showBackArrow()
        const article = await this.getArticle(target.dataset.id)
        const fromSearch = target.dataset.fromSearch === 'true'
        this.openArticle(article, fromSearch)
      } else if (target.dataset.type === 'search') {
        this.showBackArrow()
        this.openSearchResults(this.searchResults)
      }
    })

    const element = document.querySelector('.cce-widget__window__header__faq--back')
    element.addEventListener('click', async (event) => {
      if (element.dataset.type === 'category' && !this.isCategoriesAvailable) {
        document.querySelector('button[name=ccewBack]').click()
      }
      const widgetWindow = document.querySelector('.cce-widget__window')
      widgetWindow.classList.remove('extendView')

      const target = event.currentTarget
      if (target.dataset.type === 'category') {
        const articles = await this.getArticles(target.dataset.id)
        this.openCategory(target.dataset.id, articles)
        this.articles[target.dataset.id] = articles
      } else if (target.dataset.type === 'article') {
        const article = await this.getArticle(target.dataset.id)
        const fromSearch = target.dataset.fromSearch === 'true'
        this.openArticle(article, fromSearch)
      } else if (target.dataset.type === 'search') {
        this.openSearchResults(this.searchResults)
      } else {
        document.querySelector('button[name=ccewBack]').click()
      }
    })

    this.$element.addEventListener('keydown', async (event) => {
      const query = this.$element.querySelector('input[type="text"]').value
      this.$element.querySelectorAll(`.${this.styleNamespace}__suggestions`).forEach((x) => x.remove())

      if (event.key === 'Enter') {
        event.preventDefault()

        this.searchResults = await this.search(query, 'ManualSearch', 3)
        this.openSearchResults(this.searchResults)
      } else {
        clearTimeout(debounceTimer)
        debounceTimer = setTimeout(async () => {
          const suggestions = await this.suggestions(query)
          if (suggestions.length > 0) {
            let input = this.$element.querySelector('input[type="text"]')
            let suggestionsList = document.createElement('ul')
            suggestionsList.classList.add(`${this.styleNamespace}__suggestions`)
            suggestions.forEach((suggestion) => {
              let li = document.createElement('li')
              li.textContent = suggestion
              li.addEventListener('click', () => this.clickOnSuggestion(suggestion))
              suggestionsList.appendChild(li)
            })
            input.parentNode.appendChild(suggestionsList)
          }
        }, 250)
      }
    })
  }
}
